import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as TariffIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { todayCv } from '../../../utils/date';

interface BillingTariff {
  id: string;
  code: string;
  name: string;
  description: string;
  tariffType: 'fixed' | 'percentage' | 'tiered' | 'custom';
  baseValue: number;
  percentageValue?: number;
  minValue?: number;
  maxValue?: number;
  status: 'active' | 'inactive';
  applicableFrom: string;
  applicableTo?: string;
  createdAt: string;
}

// Mock data
const mockTariffs: BillingTariff[] = [
  {
    id: '1',
    code: 'TAR001',
    name: 'Taxa Fixa Padrão',
    description: 'Taxa fixa aplicada a todos os serviços básicos',
    tariffType: 'fixed',
    baseValue: 5000,
    status: 'active',
    applicableFrom: '2024-01-01',
    createdAt: '2023-12-01',
  },
  {
    id: '2',
    code: 'TAR002',
    name: 'Taxa Percentual Premium',
    description: 'Taxa de 2.5% sobre o valor da fatura',
    tariffType: 'percentage',
    baseValue: 0,
    percentageValue: 2.5,
    minValue: 2500,
    maxValue: 50000,
    status: 'active',
    applicableFrom: '2024-01-01',
    createdAt: '2023-12-01',
  },
  {
    id: '3',
    code: 'TAR003',
    name: 'Taxa Escalonada por Volume',
    description: 'Taxa que varia conforme volume de transações',
    tariffType: 'tiered',
    baseValue: 3000,
    minValue: 3000,
    maxValue: 25000,
    status: 'active',
    applicableFrom: '2024-01-01',
    createdAt: '2023-12-01',
  },
  {
    id: '4',
    code: 'TAR004',
    name: 'Taxa para Entidades Públicas',
    description: 'Taxa reduzida para entidades públicas',
    tariffType: 'fixed',
    baseValue: 2500,
    status: 'active',
    applicableFrom: '2024-01-01',
    createdAt: '2023-12-01',
  },
  {
    id: '5',
    code: 'TAR005',
    name: 'Taxa Telecomunicações',
    description: 'Taxa específica para serviços de telecomunicações',
    tariffType: 'percentage',
    baseValue: 0,
    percentageValue: 1.8,
    minValue: 1000,
    maxValue: 30000,
    status: 'active',
    applicableFrom: '2024-01-01',
    createdAt: '2023-12-01',
  },
  {
    id: '6',
    code: 'TAR006',
    name: 'Taxa Bancos',
    description: 'Taxa para transações bancárias',
    tariffType: 'percentage',
    baseValue: 0,
    percentageValue: 1.5,
    minValue: 5000,
    maxValue: 100000,
    status: 'active',
    applicableFrom: '2024-02-01',
    createdAt: '2024-01-15',
  },
  {
    id: '7',
    code: 'TAR007',
    name: 'Taxa Especial Natal 2023',
    description: 'Taxa promocional de fim de ano (desactivada)',
    tariffType: 'fixed',
    baseValue: 1500,
    status: 'inactive',
    applicableFrom: '2023-12-01',
    applicableTo: '2023-12-31',
    createdAt: '2023-11-15',
  },
  {
    id: '8',
    code: 'TAR008',
    name: 'Taxa Personalizada Enterprise',
    description: 'Taxa customizada para clientes enterprise',
    tariffType: 'custom',
    baseValue: 10000,
    status: 'active',
    applicableFrom: '2024-03-01',
    createdAt: '2024-02-20',
  },
];

const BillingTariffs: React.FC = () => {
  const [tariffs, setTariffs] = useState<BillingTariff[]>(mockTariffs);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<BillingTariff | null>(null);
  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    description: string;
    tariffType: 'fixed' | 'percentage' | 'tiered' | 'custom';
    baseValue: number;
    percentageValue: number;
    minValue: number;
    maxValue: number;
    status: 'active' | 'inactive';
    applicableFrom: string;
    applicableTo: string;
  }>({
    code: '',
    name: '',
    description: '',
    tariffType: 'fixed',
    baseValue: 0,
    percentageValue: 0,
    minValue: 0,
    maxValue: 0,
    status: 'active',
    applicableFrom: todayCv(),
    applicableTo: '',
  });

  const getTypeChip = (type: string) => {
    const config: Record<string, { color: 'primary' | 'secondary' | 'info' | 'warning'; label: string }> = {
      fixed: { color: 'primary', label: 'Fixo' },
      percentage: { color: 'secondary', label: 'Percentual' },
      tiered: { color: 'info', label: 'Escalonado' },
      custom: { color: 'warning', label: 'Personalizado' },
    };
    const { color, label } = config[type] || config.fixed;
    return <Chip size="small" color={color} label={label} />;
  };

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip size="small" color="success" label="Activo" icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label="Inactivo" icon={<InactiveIcon />} />
    );
  };

  const filteredTariffs = tariffs.filter(tariff => {
    const matchesSearch = searchTerm === '' ||
      tariff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tariff.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tariff.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || tariff.tariffType === typeFilter;
    const matchesStatus = statusFilter === 'all' || tariff.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    total: tariffs.length,
    active: tariffs.filter(t => t.status === 'active').length,
    fixed: tariffs.filter(t => t.tariffType === 'fixed').length,
    percentage: tariffs.filter(t => t.tariffType === 'percentage').length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (tariff?: BillingTariff) => {
    if (tariff) {
      setSelectedTariff(tariff);
      setFormData({
        code: tariff.code,
        name: tariff.name,
        description: tariff.description,
        tariffType: tariff.tariffType,
        baseValue: tariff.baseValue,
        percentageValue: tariff.percentageValue || 0,
        minValue: tariff.minValue || 0,
        maxValue: tariff.maxValue || 0,
        status: tariff.status,
        applicableFrom: tariff.applicableFrom,
        applicableTo: tariff.applicableTo || '',
      });
    } else {
      setSelectedTariff(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        tariffType: 'fixed',
        baseValue: 0,
        percentageValue: 0,
        minValue: 0,
        maxValue: 0,
        status: 'active',
        applicableFrom: todayCv(),
        applicableTo: '',
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedTariff(null);
  };

  const handleSave = () => {
    if (selectedTariff) {
      setTariffs(tariffs.map(t =>
        t.id === selectedTariff.id
          ? {
              ...t,
              ...formData,
              percentageValue: formData.tariffType === 'percentage' ? formData.percentageValue : undefined,
              minValue: formData.minValue > 0 ? formData.minValue : undefined,
              maxValue: formData.maxValue > 0 ? formData.maxValue : undefined,
              applicableTo: formData.applicableTo || undefined,
            }
          : t
      ));
    } else {
      const newTariff: BillingTariff = {
        id: crypto.randomUUID(),
        ...formData,
        percentageValue: formData.tariffType === 'percentage' ? formData.percentageValue : undefined,
        minValue: formData.minValue > 0 ? formData.minValue : undefined,
        maxValue: formData.maxValue > 0 ? formData.maxValue : undefined,
        applicableTo: formData.applicableTo || undefined,
        createdAt: new Date().toISOString().split('T')[0],
      };
      setTariffs([...tariffs, newTariff]);
    }
    handleCloseEdit();
  };

  const formatCurrency = (value: number) => {
    return `${Math.round(value / 100).toLocaleString('pt-PT')}$`;
  };

  const getTariffValue = (tariff: BillingTariff) => {
    if (tariff.tariffType === 'percentage') {
      return `${tariff.percentageValue}%`;
    } else if (tariff.tariffType === 'fixed') {
      return formatCurrency(tariff.baseValue);
    } else if (tariff.tariffType === 'tiered') {
      return `${formatCurrency(tariff.minValue || 0)} - ${formatCurrency(tariff.maxValue || 0)}`;
    } else {
      return 'Personalizado';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Tarifários de Faturação
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          Novo Tarifário
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TariffIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Tarifários</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                  <Typography variant="caption" color="text.secondary">Activos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TariffIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.fixed}</Typography>
                  <Typography variant="caption" color="text.secondary">Taxa Fixa</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TariffIcon color="secondary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.percentage}</Typography>
                  <Typography variant="caption" color="text.secondary">Taxa Percentual</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar tarifários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Tipo"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="fixed">Fixo</MenuItem>
                <MenuItem value="percentage">Percentual</MenuItem>
                <MenuItem value="tiered">Escalonado</MenuItem>
                <MenuItem value="custom">Personalizado</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tariffs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={100}>Código</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell width={130}>Tipo</TableCell>
                <TableCell width={150}>Valor</TableCell>
                <TableCell width={110}>Válido Desde</TableCell>
                <TableCell width={100}>Estado</TableCell>
                <TableCell width={100} align="center">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTariffs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((tariff) => (
                <TableRow key={tariff.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                      {tariff.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {tariff.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>
                      {tariff.description}
                    </Typography>
                  </TableCell>
                  <TableCell>{getTypeChip(tariff.tariffType)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {getTariffValue(tariff)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize="0.75rem">
                      {tariff.applicableFrom}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(tariff.status)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenEdit(tariff)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredTariffs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TariffIcon />
          {selectedTariff ? 'Editar Tarifário' : 'Novo Tarifário'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Código"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 10 }}
                disabled={!!selectedTariff}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Tipo de Tarifário"
                value={formData.tariffType}
                onChange={(e) => setFormData({ ...formData, tariffType: e.target.value as any })}
              >
                <MenuItem value="fixed">Taxa Fixa</MenuItem>
                <MenuItem value="percentage">Taxa Percentual</MenuItem>
                <MenuItem value="tiered">Taxa Escalonada</MenuItem>
                <MenuItem value="custom">Personalizado</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>

            {formData.tariffType === 'fixed' && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Valor Base (cêntimos)"
                  value={formData.baseValue}
                  onChange={(e) => setFormData({ ...formData, baseValue: parseInt(e.target.value) || 0 })}
                  helperText={`${Math.round(formData.baseValue / 100).toLocaleString('pt-PT')}$`}
                />
              </Grid>
            )}

            {formData.tariffType === 'percentage' && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Percentagem (%)"
                    value={formData.percentageValue}
                    onChange={(e) => setFormData({ ...formData, percentageValue: parseFloat(e.target.value) || 0 })}
                    inputProps={{ step: 0.1, min: 0, max: 100 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Valor Mínimo (cêntimos)"
                    value={formData.minValue}
                    onChange={(e) => setFormData({ ...formData, minValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(formData.minValue / 100).toLocaleString('pt-PT')}$`}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Valor Máximo (cêntimos)"
                    value={formData.maxValue}
                    onChange={(e) => setFormData({ ...formData, maxValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(formData.maxValue / 100).toLocaleString('pt-PT')}$`}
                  />
                </Grid>
              </>
            )}

            {formData.tariffType === 'tiered' && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Valor Base (cêntimos)"
                    value={formData.baseValue}
                    onChange={(e) => setFormData({ ...formData, baseValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(formData.baseValue / 100).toLocaleString('pt-PT')}$`}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Valor Mínimo (cêntimos)"
                    value={formData.minValue}
                    onChange={(e) => setFormData({ ...formData, minValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(formData.minValue / 100).toLocaleString('pt-PT')}$`}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Valor Máximo (cêntimos)"
                    value={formData.maxValue}
                    onChange={(e) => setFormData({ ...formData, maxValue: parseInt(e.target.value) || 0 })}
                    helperText={`${Math.round(formData.maxValue / 100).toLocaleString('pt-PT')}$`}
                  />
                </Grid>
              </>
            )}

            {formData.tariffType === 'custom' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Valor Referência (cêntimos)"
                  value={formData.baseValue}
                  onChange={(e) => setFormData({ ...formData, baseValue: parseInt(e.target.value) || 0 })}
                  helperText={`${Math.round(formData.baseValue / 100).toLocaleString('pt-PT')}$ - Valor de referência para cálculo personalizado`}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Aplicável Desde"
                value={formData.applicableFrom}
                onChange={(e) => setFormData({ ...formData, applicableFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Aplicável Até (opcional)"
                value={formData.applicableTo}
                onChange={(e) => setFormData({ ...formData, applicableTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Deixe vazio para aplicação indefinida"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.code || !formData.name || !formData.applicableFrom}
          >
            {selectedTariff ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingTariffs;
